/**
 * Sistema RPCode - Comandos invisibles para funciones del juego
 * Procesa comandos que ejecutan funciones pero no se muestran en el texto final
 */

import { parseRPCommand } from './chevrotain/parser'
import { RP_TAG_HANDLERS } from '../markup'

/**
 * Los comandos RP usan nombres en MAYÚSCULAS (SALIR, ATACAR).
 * El resto de bloques [ ... ] se tratan como BBCode y no se parsean aquí.
 * RP Tags ([JUTSU:id], futuros ITEM…) van al pipeline Markup — no son RP Commands.
 */
function isLikelyRPCommand(inner) {
  const trimmed = inner.trim()
  if (!trimmed || trimmed.startsWith('/')) return false
  const name = trimmed.split(/[:\s=]/, 1)[0].trim().toUpperCase()
  if (name && Object.prototype.hasOwnProperty.call(RP_TAG_HANDLERS, name)) {
    return false
  }
  if (/^[A-Z_]+(?::|$)/.test(trimmed)) return true
  if (/^[A-Z_]+$/.test(trimmed)) return true
  return false
}

class RPCodeParser {
  constructor(gameActions = {}) {
    this.commands = new Map()
    this.gameActions = gameActions
    this.initializeDefaultCommands()
  }

  /**
   * Registra un nuevo comando RPCode
   * @param {string} name - Nombre del comando (ej: 'SALIR', 'ATACAR')
   * @param {Object} config - Configuración del comando
   */
  registerCommand(name, config) {
    this.commands.set(name.toUpperCase(), {
      action: config.action || null,
      parameters: config.parameters || [],
      validate: config.validate || (() => true),
      description: config.description || '',
      visible: config.visible || false, // Por defecto los comandos son invisibles
      requiresConfirmation: config.requiresConfirmation || false,
      cooldown: config.cooldown || 0,
      lastUsed: 0,
    })
  }

  /**
   * Inicializa los comandos por defecto del juego
   */
  initializeDefaultCommands() {
    // Comandos de navegación
    this.registerCommand('SALIR', {
      action: 'leaveAdventure',
      description: 'Abandona la aventura actual',
      requiresConfirmation: true,
      validate: () => true,
    })

    this.registerCommand('ENTRAR', {
      action: 'joinAdventure',
      parameters: ['adventureId'],
      description: 'Se une a una aventura específica',
      validate: (params) => params.adventureId && params.adventureId.length > 0,
    })

    // Comandos de combate
    this.registerCommand('ATACAR', {
      action: 'attackTarget',
      parameters: ['targetId', 'weaponType'],
      description: 'Ataca a un objetivo específico',
      cooldown: 5000, // 5 segundos
      validate: (params) => params.targetId && params.targetId.length > 0,
    })

    this.registerCommand('DEFENDER', {
      action: 'defendSelf',
      description: 'Adopta posición defensiva',
      cooldown: 3000,
      validate: () => true,
    })

    this.registerCommand('HUIR', {
      action: 'fleeFromCombat',
      description: 'Intenta huir del combate',
      requiresConfirmation: true,
      validate: () => true,
    })

    // Comandos de magia
    this.registerCommand('HECHIZO', {
      action: 'castSpell',
      parameters: ['spellName', 'targetId'],
      description: 'Lanza un hechizo específico',
      cooldown: 8000,
      validate: (params) => params.spellName && params.spellName.length > 0,
    })

    this.registerCommand('CURAR', {
      action: 'healTarget',
      parameters: ['targetId', 'healAmount'],
      description: 'Cura a un objetivo',
      cooldown: 10000,
      validate: (params) => params.targetId,
    })

    // Comandos de inventario
    this.registerCommand('USAR', {
      action: 'useItem',
      parameters: ['itemId', 'targetId'],
      description: 'Usa un objeto del inventario',
      validate: (params) => params.itemId && params.itemId.length > 0,
    })

    this.registerCommand('EQUIPAR', {
      action: 'equipItem',
      parameters: ['itemId'],
      description: 'Equipa un objeto',
      validate: (params) => params.itemId && params.itemId.length > 0,
    })

    // Comandos de información
    this.registerCommand('STATS', {
      action: 'showCharacterStats',
      description: 'Muestra las estadísticas del personaje',
      visible: true, // Este comando sí es visible
      validate: () => true,
    })

    this.registerCommand('INVENTARIO', {
      action: 'showInventory',
      description: 'Muestra el inventario del personaje',
      visible: true,
      validate: () => true,
    })

    // Comandos sociales
    this.registerCommand('SUSURRAR', {
      action: 'whisperToPlayer',
      parameters: ['playerId', 'message'],
      description: 'Envía un mensaje privado a otro jugador',
      validate: (params) => params.playerId && params.message,
    })

    this.registerCommand('GRITAR', {
      action: 'shoutMessage',
      parameters: ['message'],
      description: 'Envía un mensaje a todos los jugadores cercanos',
      cooldown: 2000,
      validate: (params) => params.message && params.message.length > 0,
    })

    // Comandos de dados
    this.registerCommand('DADO', {
      action: 'rollDice',
      parameters: ['sides', 'count'],
      description: 'Lanza dados',
      visible: true,
      validate: (params) => {
        const sides = parseInt(params.sides) || 20
        const count = parseInt(params.count) || 1
        return sides > 0 && sides <= 100 && count > 0 && count <= 10
      },
    })

    this.registerCommand('TIRADA', {
      action: 'skillCheck',
      parameters: ['skill', 'difficulty'],
      description: 'Realiza una tirada de habilidad',
      visible: true,
      validate: (params) => params.skill && params.skill.length > 0,
    })
  }

  /**
   * Procesa el texto y ejecuta los comandos RPCode
   * @param {string} text - Texto con comandos RPCode
   * @param {Object} context - Contexto del juego (personaje, aventura, etc.)
   * @returns {Object} { processedText, executedCommands, errors }
   */
  async process(text, context = {}) {
    if (!text || typeof text !== 'string') {
      return { processedText: '', executedCommands: [], errors: [] }
    }

    const executedCommands = []
    const errors = []
    const outParts = []

    // Escaneo robusto de comandos: encuentra [ ... ] respetando cadenas con comillas
    let i = 0
    const N = text.length

    while (i < N) {
      const start = text.indexOf('[', i)
      if (start === -1) {
        // No hay más comandos, agregar el resto del texto
        outParts.push(text.slice(i))
        break
      }

      // Agregar texto previo al comando
      if (start > i) outParts.push(text.slice(i, start))

      // Escanear hasta ']' respetando strings con escapes dentro del bloque
      const scanned = scanBracketContent(text, start + 1)
      if (!scanned) {
        // No se cerró el corchete, tratamos el resto como texto literal
        outParts.push(text.slice(start))
        break
      }

      const { end, inner } = scanned

      // BBCode ([ooc=...], [/ooc], [b], etc.) comparte sintaxis de corchetes; no es RPCode.
      // RP Tags ([JUTSU:…]) tampoco: isLikelyRPCommand los excluye vía RP_TAG_HANDLERS.
      if (!isLikelyRPCommand(inner)) {
        outParts.push(text.slice(start, end + 1))
        i = end + 1
        continue
      }

      // Parsear con Chevrotain el contenido dentro de los corchetes (sin los corchetes)
      const { ast, lexErrors, parseErrors } = parseRPCommand(inner)

      // Determinar nombre del comando para mensajes si es posible
      const fallbackName = inner.split(':', 1)[0].trim().toUpperCase()

      if (lexErrors.length > 0 || parseErrors.length > 0 || !ast) {
        // Error de sintaxis: dejamos el texto original y registramos error
        errors.push(`Error de sintaxis en comando: [${inner}]`)
        outParts.push(text.slice(start, end + 1))
        i = end + 1
        continue
      }

      const commandName = (ast.name || fallbackName).toUpperCase()
      const command = this.commands.get(commandName)

      if (!command) {
        // Comando desconocido: mantener literal y error (compatibilidad)
        errors.push(`Comando desconocido: ${commandName}`)
        outParts.push(text.slice(start, end + 1))
        i = end + 1
        continue
      }

      // Construir parámetros desde el AST (posicionales + nombrados)
      const parameters = buildParams(command.parameters || [], ast.params || [])

      // Validación
      if (!command.validate(parameters, context)) {
        errors.push(`Parámetros inválidos para comando: ${commandName}`)
        // Mantener el texto original si inválido
        outParts.push(text.slice(start, end + 1))
        i = end + 1
        continue
      }

      // Cooldown
      const now = Date.now()
      if (command.cooldown && now - command.lastUsed < command.cooldown) {
        const remainingTime = Math.ceil((command.cooldown - (now - command.lastUsed)) / 1000)
        errors.push(`Comando ${commandName} en cooldown. Espera ${remainingTime} segundos.`)
        outParts.push(text.slice(start, end + 1))
        i = end + 1
        continue
      }

      // Ejecutar acción
      try {
        const result = await this.executeCommand(command, parameters, context)

        executedCommands.push({
          command: commandName,
          parameters,
          result,
          timestamp: now,
        })

        // Actualizar último uso
        command.lastUsed = now

        // Renderizado de salida
        if (!command.visible) {
          // Invisible: eliminar el bloque
          // No agregamos nada a outParts
        } else {
          // Visible: reemplazar por resultado visible
          const visibleResult = this.formatVisibleResult(commandName, parameters, result)
          outParts.push(visibleResult)
        }
      } catch (error) {
        errors.push(`Error ejecutando comando ${commandName}: ${error.message}`)
        // En caso de error en ejecución, mantener original
        outParts.push(text.slice(start, end + 1))
      }

      i = end + 1
    }

    let processedText = outParts.join('')

    // Limpiar solo múltiples saltos de línea consecutivos (más de 2)
    processedText = processedText.replace(/\n\s*\n\s*\n+/g, '\n\n')

    return {
      processedText,
      executedCommands,
      errors,
    }

    // ---------------- Helpers internos ----------------

    /**
     * Escanea el contenido de un bloque [ ... ] respetando cadenas con " y escapes.
     * @param {string} source
     * @param {number} fromIndex - índice después de '['
     * @returns {{end:number, inner:string}|null}
     */
    function scanBracketContent(source, fromIndex) {
      let inString = false
      let escaped = false
      let end = -1
      for (let j = fromIndex; j < source.length; j++) {
        const ch = source[j]
        if (inString) {
          if (escaped) {
            escaped = false
          } else if (ch === '\\') {
            escaped = true
          } else if (ch === '"') {
            inString = false
          }
          continue
        } else {
          if (ch === '"') {
            inString = true
            continue
          }
          if (ch === ']') {
            end = j
            break
          }
        }
      }
      if (end === -1) return null
      const inner = source.slice(fromIndex, end)
      return { end, inner }
    }

    /**
     * Construye el objeto de parámetros fusionando posicionales con nombrados.
     * - Posicionales se asignan por índice a expectedParams.
     * - Nombrados sobrescriben cualquier valor posicional.
     * @param {string[]} expectedParams
     * @param {Array} astParams
     */
    function buildParams(expectedParams, astParams) {
      const out = {}
      let posIndex = 0

      for (const p of astParams) {
        if (p.kind === 'named') {
          out[p.key] = p.value
        } else if (p.kind === 'positional') {
          const name = expectedParams[posIndex]
          if (name) {
            out[name] = p.value
            posIndex++
          }
        }
      }
      return out
    }
  }

  /**
   * Parsea los parámetros de un comando
   */
  parseParameters(parametersString, expectedParams = []) {
    const params = {}

    if (!parametersString) {
      return params
    }

    const paramPairs = parametersString.split(',').map((p) => p.trim())

    paramPairs.forEach((pair, index) => {
      if (pair.includes('=')) {
        const [key, value] = pair.split('=').map((p) => p.trim())
        params[key] = value
      } else {
        // Parámetro posicional
        const paramName = expectedParams[index]
        if (paramName) {
          params[paramName] = pair
        }
      }
    })

    return params
  }

  /**
   * Ejecuta un comando específico
   */
  async executeCommand(command, parameters, context) {
    const actionName = command.action

    if (!this.gameActions[actionName]) {
      throw new Error(`Acción no disponible: ${actionName}`)
    }

    // Ejecutar la acción del juego
    return await this.gameActions[actionName](parameters, context)
  }

  /**
   * Formatea el resultado visible de un comando
   */
  formatVisibleResult(commandName, parameters, result) {
    switch (commandName) {
      case 'STATS':
        return `[📊 **Estadísticas mostradas**]`

      case 'INVENTARIO':
        return `[🎒 **Inventario mostrado**]`

      case 'DADO':
        const sides = parameters.sides || 20
        const count = parameters.count || 1
        const rolls = result.rolls || []
        const total = result.total || 0
        return `[🎲 **Dados (${count}d${sides})**: ${rolls.join(', ')} = **${total}**]`

      case 'TIRADA':
        const skill = parameters.skill
        const roll = result.roll || 0
        const modifier = result.modifier || 0
        const success = result.success || false
        const emoji = success ? '✅' : '❌'
        return `[${emoji} **Tirada de ${skill}**: ${roll} + ${modifier} = **${roll + modifier}** (${success ? 'Éxito' : 'Fallo'})]`

      default:
        return `[⚡ **${commandName}** ejecutado]`
    }
  }

  /**
   * Obtiene la lista de comandos disponibles
   */
  getAvailableCommands() {
    return Array.from(this.commands.entries()).map(([name, config]) => ({
      name,
      description: config.description,
      parameters: config.parameters,
      visible: config.visible,
      cooldown: config.cooldown,
    }))
  }

  /**
   * Obtiene información sobre un comando específico
   */
  getCommandInfo(commandName) {
    return this.commands.get(commandName.toUpperCase()) || null
  }

  /**
   * Elimina un comando
   */
  removeCommand(commandName) {
    this.commands.delete(commandName.toUpperCase())
  }

  /**
   * Verifica si un comando está en cooldown
   */
  isCommandOnCooldown(commandName) {
    const command = this.commands.get(commandName.toUpperCase())
    if (!command || !command.cooldown) return false

    const now = Date.now()
    return now - command.lastUsed < command.cooldown
  }

  /**
   * Obtiene el tiempo restante de cooldown
   */
  getCooldownRemaining(commandName) {
    const command = this.commands.get(commandName.toUpperCase())
    if (!command || !command.cooldown) return 0

    const now = Date.now()
    const remaining = command.cooldown - (now - command.lastUsed)
    return Math.max(0, remaining)
  }
}

export default RPCodeParser
